/**
 * UIPreferencesContext — единый источник истины для UI-настроек,
 * которые реально влияют на интерфейс (без бэкенда):
 *
 *  - theme: 'light' | 'dark' | 'auto'
 *  - sidebarCollapsed: boolean
 *  - compactMode: boolean — добавляет класс `compact` на <body>
 *  - showTooltips: boolean
 *
 * Хранится в localStorage. При изменении немедленно применяется к DOM
 * (theme → html.classList, compact → body.classList).
 */

import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from 'react';
import { setTheme as applyAndStoreTheme, applyPrimaryColor } from 'shared/theme/applyTheme';

const STORAGE_KEY = 'ui_preferences_v1';

const DEFAULTS = {
    theme: 'light',
    sidebarCollapsed: false,
    compactMode: false,
    showTooltips: true,
    primaryColor: null, // null = дефолтный брендовый цвет темы (см. tokens.css)
};

function resolveEffectiveTheme(theme) {
    if (theme !== 'auto') return theme;
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

function loadFromStorage() {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (!raw) return { ...DEFAULTS };
        const parsed = JSON.parse(raw);
        return { ...DEFAULTS, ...parsed };
    } catch {
        return { ...DEFAULTS };
    }
}

function persist(prefs) {
    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs));
    } catch {
        /* quota — игнорируем */
    }
}

/**
 * Применить настройки к DOM. Должна вызываться при изменении prefs.
 */
function applyToDom(prefs) {
    // Тема: 'auto' → следуем system preference; иначе явная.
    const effectiveTheme = resolveEffectiveTheme(prefs.theme);
    applyAndStoreTheme(effectiveTheme);

    // Кастомный акцентный цвет — переопределяет --primary/--primary-700 из tokens.css.
    applyPrimaryColor(prefs.primaryColor);

    // Compact mode → body.compact
    document.body.classList.toggle('compact', !!prefs.compactMode);

    // Tooltips флаг — компоненты сами читают prefs.showTooltips.

    return effectiveTheme;
}

const UIPreferencesContext = createContext(undefined);

export function UIPreferencesProvider({ children }) {
    const [prefs, setPrefs] = useState(() => loadFromStorage());
    const [effectiveTheme, setEffectiveTheme] = useState(() => resolveEffectiveTheme(prefs.theme));

    // Применяем при mount и при каждом изменении.
    useEffect(() => {
        setEffectiveTheme(applyToDom(prefs));
        persist(prefs);
    }, [prefs]);

    // Если выбран 'auto' — слушаем смену темы ОС.
    useEffect(() => {
        if (prefs.theme !== 'auto') return undefined;
        const mql = window.matchMedia('(prefers-color-scheme: dark)');
        const handler = () => setEffectiveTheme(applyToDom(prefs));
        mql.addEventListener('change', handler);
        return () => mql.removeEventListener('change', handler);
    }, [prefs.theme]); // eslint-disable-line react-hooks/exhaustive-deps

    const update = useCallback((patch) => {
        setPrefs((prev) => ({ ...prev, ...patch }));
    }, []);

    const reset = useCallback(() => {
        setPrefs({ ...DEFAULTS });
    }, []);

    const value = useMemo(
        () => ({ prefs, effectiveTheme, update, reset }),
        [prefs, effectiveTheme, update, reset]
    );

    return (
        <UIPreferencesContext.Provider value={value}>
            {children}
        </UIPreferencesContext.Provider>
    );
}

export function useUIPreferences() {
    const ctx = useContext(UIPreferencesContext);
    if (ctx === undefined) {
        throw new Error('useUIPreferences must be used within UIPreferencesProvider');
    }
    return ctx;
}
